import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from '@/components/ui/sidebar'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useGetItems } from '@/features/items/hooks/useItem'
import { useGetRepairs } from '@/features/repairs/hooks/useRepair'
import { useGetActiveTransferRequests } from '@/features/requests/hooks/useTransferRequest'
import { useGetConfigs } from '@/features/settings/hooks/useConfig'
import { AppConfigKey } from '@/features/settings/types/config'
import { useImageUrl } from '@/hooks/useFile'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { NAV_ITEMS, NavItem } from '../config/navigation'
import { UserMenu } from './UserMenu'

export function AppSidebar() {
  const { t } = useTranslation()
  const location = useLocation()
  const { currentUser } = useAuth()
  const isPasswordChangeRequired = currentUser?.isPasswordChanged === false
  const { data: activeTransfers = [] } = useGetActiveTransferRequests(undefined, !isPasswordChangeRequired)
  const { data: repairs = [] } = useGetRepairs(undefined, !isPasswordChangeRequired)
  const { data: items = [] } = useGetItems(undefined, !isPasswordChangeRequired)
  const { data: configs } = useGetConfigs()
  const logoImageId = configs?.[AppConfigKey.HospitalLogo]
  const { data: logoUrl } = useImageUrl(logoImageId || undefined)

  const openRepairs = repairs.filter(repair => repair.status !== 'Completed' && repair.status !== 'Closed').length

  const badgeMap: Record<string, number | null> = {
    '/equipment': items.length,
    '/requests': activeTransfers.length,
    '/repairs': openRepairs,
  }

  const canAccess = (item: NavItem) => {
    if (!item.roles) return true
    if (!currentUser) return false

    return item.roles.includes(currentUser.role)
  }

  const mainNav = NAV_ITEMS.filter(i => i.group === 'main' && canAccess(i))
  const workflowNav = NAV_ITEMS.filter(i => i.group === 'workflow' && canAccess(i))
  const accountNav = NAV_ITEMS.filter(i => i.group === 'account' && canAccess(i))
  const adminNav = NAV_ITEMS.filter(i => i.group === 'admin' && canAccess(i))

  const renderMenu = (items: NavItem[]) => (
    <SidebarMenu>
      {items.map(item => {
        const count = badgeMap[item.href]

        return (
          <SidebarMenuItem key={item.titleKey}>
            <SidebarMenuButton
              asChild
              isActive={location.pathname === item.href || location.pathname.startsWith(item.href + '/')}
              tooltip={t(item.titleKey)}
            >
              <Link data-testid={item.href === '/' ? 'nav-overview' : `nav-${item.href.slice(1)}`} to={item.href}>
                <item.icon className="size-4" />
                <span>{t(item.titleKey)}</span>
              </Link>
            </SidebarMenuButton>

            {count != null && count > 0 && <SidebarMenuBadge>{count}</SidebarMenuBadge>}
          </SidebarMenuItem>
        )
      })}
    </SidebarMenu>
  )

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="p-2 group-data-[collapsible=icon]:p-0">
          <div className="flex items-center gap-3 w-full overflow-hidden group-data-[collapsible=icon]:justify-center">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg text-primary-foreground">
              <img
                src={logoUrl ?? '/resources/logo.png'}
                alt="MTrack logo"
                className="h-full w-full object-contain 
              group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8"
              />
            </div>
            <div className="flex flex-col group-data-[collapsible=icon]:hidden">
              <span className="text-sm font-semibold text-foreground">{t('common.web.title')}</span>
              <span className="text-[10px] text-muted-foreground">{t('common.web.description')}</span>
            </div>
          </div>
        </div>
        <SidebarSeparator />
      </SidebarHeader>
      <SidebarContent>
        {!isPasswordChangeRequired && mainNav.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>{t('navigation.menu.main')}</SidebarGroupLabel>
            <SidebarGroupContent>{renderMenu(mainNav)}</SidebarGroupContent>
          </SidebarGroup>
        )}
        {!isPasswordChangeRequired && workflowNav.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>{t('navigation.menu.operations')}</SidebarGroupLabel>
            <SidebarGroupContent>{renderMenu(workflowNav)}</SidebarGroupContent>
          </SidebarGroup>
        )}
        {accountNav.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>{t('navigation.menu.account')}</SidebarGroupLabel>
            <SidebarGroupContent>{renderMenu(accountNav)}</SidebarGroupContent>
          </SidebarGroup>
        )}
        {!isPasswordChangeRequired && adminNav.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>{t('navigation.menu.admin')}</SidebarGroupLabel>
            <SidebarGroupContent>{renderMenu(adminNav)}</SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter className="group-data-[collapsible=icon]:hidden">
        <SidebarSeparator />
        <div className="px-2 py-2">
          <UserMenu />
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}
